<template>
  <div class="content md room-status-content">
    <div class="flex-box vertical flex-grow">
      <div class="stat-row">
        <div class="stat-card">
          <div class="stat-num">{{ listSum }}</div>
          <div class="stat-label">总房间数</div>
        </div>
        <div class="stat-card">
          <div class="stat-num assigned">{{ assignedSum }}</div>
          <div class="stat-label">已分配</div>
        </div>
        <div class="stat-card">
          <div class="stat-num">{{ listSum - assignedSum }}</div>
          <div class="stat-label">未分配</div>
        </div>
      <div class="stat-filter">
        <a-radio-group v-model="filter" button-style="solid">
          <a-radio-button value="all">全部</a-radio-button>
          <a-radio-button value="unlockable">可开锁</a-radio-button>
          <a-radio-button value="assigned">已分配</a-radio-button>
          <a-radio-button value="unassigned">未分配</a-radio-button>
        </a-radio-group>
        <a-input-search v-model="keyword" placeholder="搜索房间名" style="width: 220px" allow-clear/>
      </div>
      </div>
    <div class="data-area">
      <a-table
        ref="dataTable"
        row-key="roomId"
        :columns="columns"
        :data-source="filteredList"
        :loading="loading"
        :scroll="{y: h}"
        :pagination="{ pageSize: 20, showSizeChanger: true, showTotal: total => `共 ${total} 间` }">
      </a-table>
    </div>
    <a-modal
      :title="'入住记录 - ' + recordRoom"
      :visible="recordVisible"
      :width="700"
      :footer="null"
      @cancel="recordVisible = false">
      <div class="record-list">
        <div class="record-item" v-for="(r, i) in recordList" :key="i">
          <div class="record-head">
            <span class="record-name">{{r.name}}</span>
            <span class="record-phone">{{r.phone}}</span>
            <a-tag v-if="r.timeStatus" :color="getUnlockStatusView(r.timeStatus).color" class="record-state">{{getUnlockStatusView(r.timeStatus).text}}</a-tag>
            <span v-else class="record-state">未限制</span>
          </div>
          <div class="record-line"><span class="assign-label">入住时间：</span>{{r.startTime || '—'}} 至 {{r.endTime || '—'}}</div>
          <div class="record-line"><span class="assign-label">申请单号：</span>{{r.applyId}}<span class="assign-label" style="margin-left: 12px">申请时间：</span>{{r.applyTime || '—'}}<span class="assign-label" style="margin-left: 12px">入住人数：</span>{{r.num || '—'}}</div>
        </div>
        <a-empty v-if="recordList.length == 0" description="暂无入住记录"/>
      </div>
    </a-modal>
    </div>
  </div>
</template>

<script>
    // 房态：1闲置 2出租 4到期 5打扫 6维修
    const roomStateText = {1: '闲置', 2: '出租', 4: '到期', 5: '打扫', 6: '维修'};
    const roomStateColor = {1: 'green', 2: 'blue', 4: 'orange', 5: 'cyan', 6: 'volcano'};
    // 开锁状态（与小程序端 timeStatus 拦截逻辑一致）
    const unlockStatusView = {
        ing: {text: '可开锁', color: 'green'},
        before: {text: '未开始', color: 'orange'},
        after: {text: '已结束', color: 'red'},
    };

    export default {
        name: "TemplateRoomStatus",
        data() {
            return {
                loading: false,
                // 表格体滚动高度（setHeight 动态计算）
                h: 500,
                // 接口统计（文档 3.6 room-allocations）
                listSum: 0,
                assignedSum: 0,
                // 归一化后的全量房间
                rooms: [],
                // 筛选
                filter: 'all',
                keyword: '',
                // 入住记录弹窗
                recordVisible: false,
                recordRoom: '',
                recordList: [],
            }
        },
        computed: {
            filteredList() {
                let list = this.rooms;
                if(this.filter === 'assigned') {
                    list = list.filter(room => room.assigned.length > 0);
                } else if(this.filter === 'unassigned') {
                    list = list.filter(room => room.assigned.length == 0);
                } else if(this.filter === 'unlockable') {
                    // 可开锁：存在处于入住时段内（或无时间限制）的分配
                    list = list.filter(room => this.getRoomUnlockState(room) === 'ing');
                }
                let keyword = this.keyword.trim().toLowerCase();
                if(keyword) {
                    list = list.filter(room => this.getRoomLabel(room).toLowerCase().indexOf(keyword) > -1);
                }
                return list;
            },
            columns() {
                return [
                    {title: '房间', dataIndex: 'roomName', customRender: (text, record) => this.getRoomLabel(record)},
                    {title: '房态', dataIndex: 'roomState', width: 90, customRender: text => <a-tag color={roomStateColor[text]}>{roomStateText[text] || '未知'}</a-tag>},
                    {title: '门锁', dataIndex: 'lockState', width: 130, customRender: (text, record) => this.renderLockState(record)},
                    {title: '分配状态', key: 'assignState', width: 200, customRender: (text, record) => {
                        // 只统计有效分配（可开锁/未开始），全部过期的显示已结束
                        const active = record.assigned.filter(item => item.timeStatus === 'ing' || item.timeStatus === 'before');
                        if(record.assigned.length == 0) {
                            return <a-tag>未分配</a-tag>;
                        }
                        if(active.length == 0) {
                            return <a-tag color="red">已结束（{record.assigned.length}）</a-tag>;
                        }
                        // 有效分配按状态分组：[可开锁（n）][未开始（n）]
                        const groups = {};
                        active.forEach(item => {
                            const key = item.timeStatus === '' ? 'ing' : item.timeStatus;
                            groups[key] = (groups[key] || 0) + 1;
                        });
                        return ['ing', 'before'].filter(key => groups[key]).map(key => {
                            const view = unlockStatusView[key];
                            return <a-tag color={view.color}>{view.text}（{groups[key]}）</a-tag>;
                        });
                    }},
                    {title: '入住人', key: 'users', customRender: (text, record) => this.renderUsers(record)},
                    {title: '操作', key: 'action', width: 90, customRender: (text, record) => record.assigned.length > 0
                        ? <span class="txt-btn" onClick={() => this.showRecords(record)}>入住记录</span>
                        : <span style="color: rgba(0,0,0,0.45)">—</span>},
                ];
            }
        },
        created() {
            this.getList();
        },
        mounted() {
            this.setHeight();
            window.addEventListener("resize", this.setHeight, false);
        },
        beforeDestroy() {
            window.removeEventListener("resize", this.setHeight, false);
        },
        methods: {
            // 表格体高度 = 表格区域高度 - 表头(54) - 分页条(64)，数据多时表内滚动、表头固定
            setHeight() {
                let dataTable = this.$refs.dataTable;
                if(dataTable) {
                    let area = this.$el.querySelector(".data-area");
                    this.h = area.clientHeight - 54 - 64;
                }
            },
            // 全量房间与分配情况（文档 3.6）：GET /admin/bluetooth-lock/room-allocations
            // 返回 {error, data: {list: [{...房间字段, assigned: [{applyId, phone, roomName, applyTime, detail}]}], listSum, assignedSum}}
            getList() {
                this.loading = true;
                this.$axios({url: '/admin/bluetooth-lock/room-allocations', noTempleFilter: true}).then(res => {
                    let data = res.data || {};
                    let list = Array.isArray(data.list) ? data.list : [];
                    this.rooms = list.map(room => this.normalizeRoom(room));
                    this.listSum = data.listSum !== undefined ? data.listSum : list.length;
                    this.assignedSum = data.assignedSum !== undefined ? data.assignedSum : list.filter(room => room.assigned.length > 0).length;
                    this.loading = false;
                }, () => {
                    this.loading = false;
                });
            },
            // 归一化房间数据：assigned 中解析 detail（入住人/入住时间），联调时字段有出入只需调整此函数
            normalizeRoom(room) {
                let assigned = Array.isArray(room.assigned) ? room.assigned : [];
                return {
                    roomId: room.roomId || room.id || '',
                    roomName: room.roomName || room.name || '',
                    buildingName: room.buildingName || '',
                    floorName: room.floorName || '',
                    roomState: room.roomState,
                    lockState: room.lockState,
                    electricNum: room.electricNum,
                    assigned: assigned.map(item => this.normalizeAssigned(item)),
                };
            },
            normalizeAssigned(item) {
                let detail = this.parseDetail(item.detail);
                let phone = item.phone || '';
                // 姓名（文档 4)：后端下发实际入住人 name（来自 select.rooms[].name）；
                // 老数据回退：按手机号从入住人员列表匹配 → 申请人姓名
                let checkinUsers = Array.isArray(detail.checkin_user_list) ? detail.checkin_user_list : [];
                let user = checkinUsers.find(u => u.mobile === phone);
                let name = item.name || (user && user.name) || detail.name || '';
                return {
                    applyId: item.applyId,
                    phone: phone,
                    name: name,
                    num: detail.num || '',
                    startTime: detail.start_time || '',
                    endTime: detail.end_time || '',
                    // 开锁状态快照：before 未开始 / ing 可开锁 / after 已结束 / '' 未限制
                    timeStatus: this.getTimeStatus(detail.start_time, detail.end_time),
                    // 入住开始时间戳（入住记录倒序用）
                    startTs: detail.start_time ? (new Date(String(detail.start_time).replace(/-/g, '/')).getTime() || 0) : 0,
                    applyTime: item.applyTime || '',
                };
            },
            /**
             * 计算单条分配的开锁状态（判定依据与小程序端一致：当前时间是否处于入住时间段内）
             * 时间缺失或格式异常返回 ''（不限制）
             */
            getTimeStatus(startTime, endTime) {
                if (!startTime || !endTime) return '';
                const start = new Date(String(startTime).replace(/-/g, '/')).getTime();
                const end = new Date(String(endTime).replace(/-/g, '/')).getTime();
                if (isNaN(start) || isNaN(end)) return '';
                const now = Date.now();
                if (now < start) return 'before';
                if (now > end) return 'after';
                return 'ing';
            },
            getUnlockStatusView(state) {
                return unlockStatusView[state] || {text: '未限制', color: 'default'};
            },
            /**
             * 房间级开锁状态汇总（多分配时取最优）：
             * 任一可开锁 → ing；否则有未开始 → before；有分配但全部已结束 → after；无分配 → unassigned
             */
            getRoomUnlockState(record) {
                if(record.assigned.length == 0) {
                    return 'unassigned';
                }
                let states = record.assigned.map(item => item.timeStatus);
                if(states.some(state => state === 'ing' || state === '')) {
                    return 'ing';
                }
                if(states.some(state => state === 'before')) {
                    return 'before';
                }
                return 'after';
            },
            // detail 兼容对象与 JSON 字符串两种形态
            parseDetail(detail) {
                if(!detail) return {};
                if(typeof detail === 'object') return detail;
                try {
                    return JSON.parse(detail);
                } catch (e) {
                    return {};
                }
            },
            // 房间展示名：楼栋-楼层-房号（缺省层级自动跳过）
            getRoomLabel(room) {
                return [room.buildingName, room.floorName, room.roomName].filter(item => item).join('-');
            },
            renderLockState(record) {
                let text = record.lockState == 3 ? '锁在线' : '锁离线';
                if(record.electricNum !== undefined && record.electricNum !== null && record.electricNum !== '') {
                    text += ` ${record.electricNum}%`;
                }
                return <a-tag color={record.lockState == 3 ? 'green' : 'orange'}>{text}</a-tag>;
            },
            renderUsers(record) {
                // 只展示当前可开锁与后续可开锁（未开始）的分配，过期记录移入"入住记录"弹窗
                const active = record.assigned.filter(item => item.timeStatus === 'ing' || item.timeStatus === 'before');
                if(active.length == 0) {
                    return <span style="color: rgba(0,0,0,0.45)">—</span>;
                }
                return active.map(item => {
                    return <div class="user-line" key={item.applyId + '-' + item.phone}>
                        <div class="user-main">
                            <span class={["user-state", item.timeStatus]} title={item.timeStatus === 'ing' ? '可开锁' : '未开始'}></span>
                            {item.name ? <span class="user-name">{item.name}</span> : null}
                            <span>{item.phone}</span>
                        </div>
                        <div class="user-time">{item.startTime || '—'} 至 {item.endTime || '—'}</div>
                    </div>;
                });
            },
            // 查看房间全部入住记录（含已过期），按入住开始时间倒序
            showRecords(record) {
                this.recordRoom = this.getRoomLabel(record);
                this.recordList = [...record.assigned].sort((a, b) => b.startTs - a.startTs);
                this.recordVisible = true;
            },
        }
    }
</script>

<style scoped lang="less">
    // 视口定高：content 填满路由区域，data-area 高度与表格内容无关，setHeight 任何时刻都精确
    .room-status-content {
        height: 100%;
        display: flex;
        flex-direction: column;
        .flex-box {
            flex: 1;
            min-height: 0;
        }
        .data-area {
            flex: 1;
            min-height: 0;
        }
    }
    .stat-row {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        margin-bottom: 16px;
    }
    .stat-card {
        min-width: 120px;
        margin-right: 16px;
        padding: @padding-sm @padding-lg;
        background-color: @component-background;
        border-radius: 4px;
        text-align: center;
    }
    .stat-num {
        font-size: 24px;
        font-weight: 600;
        line-height: 1.4;
        &.assigned {
            color: @success-color;
        }
    }
    .stat-label {
        color: @text-color-secondary;
    }
    .stat-filter {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
    }
    .assign-line {
        line-height: 22px;
    }
    .assign-label {
        color: @text-color-secondary;
    }
    .user-line {
        line-height: 22px;
        .user-main {
            display: flex;
            align-items: center;
            // 状态圆点：绿=可开锁（ing）、橙=未开始（before），颜色与分配状态列一致
            .user-state {
                width: 8px;
                height: 8px;
                border-radius: 50%;
                margin-right: 6px;
                flex-shrink: 0;
                &.ing {
                    background-color: @success-color;
                }
                &.before {
                    background-color: #fa8c16;
                }
            }
            .user-name {
                font-weight: 600;
                margin-right: @padding-xs;
            }
        }
        .user-time {
            font-size: 12px;
            color: @text-color-secondary;
        }
    }
    .record-list {
        max-height: 60vh;
        overflow: auto;
    }
    .record-item {
        padding: @padding-sm;
        border: 1px solid @border-color-split;
        border-radius: 4px;
        &:not(:first-child) {
            margin-top: @padding-xs;
        }
    }
    .record-head {
        display: flex;
        align-items: center;
        margin-bottom: @padding-xs;
        .record-name {
            font-weight: 600;
            margin-right: @padding-xs;
        }
        .record-phone {
            margin-right: @padding-xs;
        }
        .record-state {
            margin-right: 0;
        }
    }
    .record-line {
        line-height: 22px;
    }
    .assign-label {
        color: @text-color-secondary;
    }
</style>
